/**
 * Memory Management Component
 * View, search, and manage long-term memories extracted from conversations
 */

import React, { useState, useEffect } from 'react';
import { orchestratorApi } from '../api/orchestrator';
import './GoogleCalendarAuth.css'; // Reuse styles

// Types
type MemoryType = 'preference' | 'history' | 'risk' | 'escalation' | 'booking_context' | 'profile';
type Sensitivity = 'normal' | 'restricted' | 'confidential';

interface MemoryItem {
  id: string;
  tenant_id: string;
  contact_id: string;
  entity_id?: string;
  memory_type: MemoryType;
  key: string;
  value_text?: string;
  value_json: Record<string, any>;
  sensitivity: Sensitivity;
  confidence: number;
  expires_at?: string;
  created_at: string;
  updated_at: string;
}

interface MemoryStats {
  total_memories: number;
  by_type: Record<MemoryType, number>;
  by_sensitivity: Record<Sensitivity, number>;
  avg_confidence: number;
  pending_expiry: number;
}

const MEMORY_TYPE_LABELS: Record<MemoryType, string> = {
  preference: 'Preference',
  history: 'History',
  risk: 'Risk',
  escalation: 'Escalation',
  booking_context: 'Booking Context',
  profile: 'Profile',
};

const MEMORY_TYPE_COLORS: Record<MemoryType, string> = {
  preference: '#3b82f6',
  history: '#8b5cf6',
  risk: '#ef4444',
  escalation: '#f59e0b',
  booking_context: '#10b981',
  profile: '#6b7280',
};

const SENSITIVITY_LABELS: Record<Sensitivity, string> = {
  normal: 'Normal',
  restricted: 'Restricted',
  confidential: 'Confidential',
};

export const MemoryManagement: React.FC = () => {
  // State
  const [memories, setMemories] = useState<MemoryItem[]>([]);
  const [stats, setStats] = useState<MemoryStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState<MemoryType | 'all'>('all');
  const [selectedMemory, setSelectedMemory] = useState<MemoryItem | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info', text: string } | null>(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [limit] = useState(20);

  useEffect(() => {
    loadStats();
    loadMemories();
  }, [selectedType, page, searchQuery]);

  const loadStats = async () => {
    try {
      const response = await fetch('/api/memory/stats', {
        headers: await orchestratorApi.getAuthHeaders()
      });

      const data = await response.json();

      if (data.ok) {
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Failed to load memory stats:', error);
    }
  };

  const loadMemories = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: (page * limit).toString(),
      });

      if (selectedType !== 'all') {
        params.append('memory_types', selectedType);
      }

      if (searchQuery) {
        params.append('q', searchQuery);
      }

      const response = await fetch(`/api/memory/search?${params}`, {
        headers: await orchestratorApi.getAuthHeaders()
      });

      const data = await response.json();

      if (data.ok) {
        setMemories(data.memories || []);
      } else {
        showMessage('error', data.error || 'Failed to load memories');
      }
    } catch (error) {
      console.error('Failed to load memories:', error);
      showMessage('error', 'Failed to load memories');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMemory = async (memoryId: string) => {
    if (!confirm('Are you sure you want to delete this memory?')) {
      return;
    }

    try {
      const response = await fetch(`/api/memory/${memoryId}`, {
        method: 'DELETE',
        headers: await orchestratorApi.getAuthHeaders()
      });

      const data = await response.json();

      if (data.ok) {
        showMessage('success', 'Memory deleted successfully');
        setSelectedMemory(null);
        await loadMemories();
        await loadStats();
      } else {
        showMessage('error', data.error || 'Failed to delete memory');
      }
    } catch (error) {
      console.error('Failed to delete memory:', error);
      showMessage('error', 'Failed to delete memory');
    }
  };

  const handleCleanup = async () => {
    if (!confirm('Are you sure you want to clean up expired memories?')) {
      return;
    }

    try {
      const response = await fetch('/api/memory/cleanup', {
        method: 'POST',
        headers: {
          ...(await orchestratorApi.getAuthHeaders()),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });

      const data = await response.json();

      if (data.ok) {
        showMessage('success', `Cleaned up ${data.cleaned} expired memories`);
        await loadMemories();
        await loadStats();
      } else {
        showMessage('error', data.error || 'Failed to cleanup memories');
      }
    } catch (error) {
      console.error('Failed to cleanup memories:', error);
      showMessage('error', 'Failed to cleanup memories');
    }
  };

  const showMessage = (type: 'success' | 'error' | 'info', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const formatValue = (memory: MemoryItem): string => {
    if (memory.value_text) {
      return memory.value_text;
    }
    return JSON.stringify(memory.value_json, null, 2);
  };

  const isExpired = (memory: MemoryItem): boolean => {
    return memory.expires_at ? new Date(memory.expires_at) < new Date() : false;
  };

  return (
    <div className="google-calendar-auth">
      <div className="gca-header">
        <h2>🧠 Long-Term Memory Management</h2>
        <p className="gca-description">
          View and manage memories extracted from customer conversations.
          Memories are organized by type and include preferences, history, risks, and more.
        </p>
      </div>

      {message && (
        <div className={`gca-message gca-message-${message.type}`}>
          {message.text}
        </div>
      )}

      {/* Statistics Cards */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '15px', marginBottom: '20px' }}>
          <div className="gca-card" style={{ padding: '15px', textAlign: 'center' }}>
            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#3b82f6' }}>{stats.total_memories}</div>
            <div style={{ fontSize: '14px', color: '#6b7280' }}>Total Memories</div>
          </div>
          <div className="gca-card" style={{ padding: '15px', textAlign: 'center' }}>
            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#10b981' }}>{stats.avg_confidence.toFixed(1)}%</div>
            <div style={{ fontSize: '14px', color: '#6b7280' }}>Avg Confidence</div>
          </div>
          <div className="gca-card" style={{ padding: '15px', textAlign: 'center' }}>
            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#f59e0b' }}>{stats.pending_expiry}</div>
            <div style={{ fontSize: '14px', color: '#6b7280' }}>Pending Expiry</div>
          </div>
        </div>
      )}

      {/* By Type Statistics */}
      {stats && (
        <div className="gca-card" style={{ marginBottom: '20px' }}>
          <h3>Memories by Type</h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
            {Object.entries(stats.by_type).map(([type, count]) => (
              <div
                key={type}
                style={{
                  padding: '8px 12px',
                  borderRadius: '4px',
                  backgroundColor: MEMORY_TYPE_COLORS[type as MemoryType] + '20',
                  border: `1px solid ${MEMORY_TYPE_COLORS[type as MemoryType]}`,
                }}
              >
                <span style={{ fontWeight: 'bold', color: MEMORY_TYPE_COLORS[type as MemoryType] }}>{count}</span>
                {' '}{MEMORY_TYPE_LABELS[type as MemoryType]}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="gca-card" style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            type="text"
            placeholder="Search memories..."
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
            style={{ flex: 1, minWidth: '200px', padding: '10px', border: '1px solid #ddd', borderRadius: '4px' }}
          />

          <select
            value={selectedType}
            onChange={(e) => { setSelectedType(e.target.value as MemoryType | 'all'); setPage(0); }}
            style={{ padding: '10px', border: '1px solid #ddd', borderRadius: '4px' }}
          >
            <option value="all">All Types</option>
            <option value="preference">Preferences</option>
            <option value="history">History</option>
            <option value="risk">Risks</option>
            <option value="escalation">Escalations</option>
            <option value="booking_context">Booking Context</option>
            <option value="profile">Profile</option>
          </select>

          <button
            onClick={() => { setSearchQuery(''); setSelectedType('all'); setPage(0); }}
            className="gca-button gca-button-small"
          >
            Reset
          </button>

          <button
            onClick={handleCleanup}
            className="gca-button gca-button-small gca-button-danger"
          >
            🧹 Cleanup Expired
          </button>
        </div>
      </div>

      {/* Memories List */}
      <div className="gca-card">
        <h3>Memories {selectedType !== 'all' && `(${MEMORY_TYPE_LABELS[selectedType]})`}</h3>

        {loading ? (
          <p>Loading...</p>
        ) : memories.length === 0 ? (
          <p className="gca-empty">No memories found.</p>
        ) : (
          <table className="gca-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Key</th>
                <th>Value</th>
                <th>Confidence</th>
                <th>Sensitivity</th>
                <th>Expires</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {memories.map((memory) => (
                <tr
                  key={memory.id}
                  style={{
                    opacity: isExpired(memory) ? 0.5 : 1,
                    backgroundColor: isExpired(memory) ? '#fee2e2' : undefined,
                  }}
                >
                  <td>
                    <span
                      style={{
                        padding: '2px 8px',
                        borderRadius: '4px',
                        backgroundColor: MEMORY_TYPE_COLORS[memory.memory_type] + '20',
                        color: MEMORY_TYPE_COLORS[memory.memory_type],
                        fontSize: '12px',
                        fontWeight: 'bold',
                      }}
                    >
                      {MEMORY_TYPE_LABELS[memory.memory_type]}
                    </span>
                  </td>
                  <td>{memory.key}</td>
                  <td style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {formatValue(memory)}
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <div style={{ width: '50px', height: '6px', backgroundColor: '#e5e7eb', borderRadius: '3px' }}>
                        <div
                          style={{
                            width: `${memory.confidence}%`,
                            height: '100%',
                            backgroundColor: memory.confidence >= 70 ? '#10b981' : memory.confidence >= 40 ? '#f59e0b' : '#ef4444',
                            borderRadius: '3px',
                          }}
                        />
                      </div>
                      <span style={{ fontSize: '12px' }}>{memory.confidence}%</span>
                    </div>
                  </td>
                  <td>
                    <span
                      style={{
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontSize: '11px',
                        backgroundColor: memory.sensitivity === 'confidential' ? '#fecaca' : memory.sensitivity === 'restricted' ? '#fef3c7' : '#d1fae5',
                      }}
                    >
                      {SENSITIVITY_LABELS[memory.sensitivity]}
                    </span>
                  </td>
                  <td>
                    {memory.expires_at ? (
                      <span style={{ fontSize: '12px', color: isExpired(memory) ? '#ef4444' : '#6b7280' }}>
                        {new Date(memory.expires_at).toLocaleDateString()}
                      </span>
                    ) : (
                      <span style={{ color: '#9ca3af' }}>Never</span>
                    )}
                  </td>
                  <td>
                    <button
                      onClick={() => setSelectedMemory(memory)}
                      className="gca-button gca-button-small"
                    >
                      View
                    </button>
                    <button
                      onClick={() => handleDeleteMemory(memory.id)}
                      className="gca-button gca-button-small gca-button-danger"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {memories.length >= limit && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '20px' }}>
            <button
              onClick={() => setPage(Math.max(0, page - 1))}
              disabled={page === 0}
              className="gca-button gca-button-small"
            >
              Previous
            </button>
            <span style={{ padding: '10px' }}>Page {page + 1}</span>
            <button
              onClick={() => setPage(page + 1)}
              disabled={memories.length < limit}
              className="gca-button gca-button-small"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Memory Detail Modal */}
      {selectedMemory && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setSelectedMemory(null)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '20px',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '80vh',
              overflow: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2>Memory Details</h2>
              <button
                onClick={() => setSelectedMemory(null)}
                style={{ border: 'none', background: 'none', fontSize: '24px', cursor: 'pointer' }}
              >
                ×
              </button>
            </div>

            <div style={{ display: 'grid', gap: '15px' }}>
              <div>
                <strong>Type:</strong>{' '}
                <span style={{ color: MEMORY_TYPE_COLORS[selectedMemory.memory_type] }}>
                  {MEMORY_TYPE_LABELS[selectedMemory.memory_type]}
                </span>
              </div>

              <div>
                <strong>Key:</strong> {selectedMemory.key}
              </div>

              <div>
                <strong>Value:</strong>
                <pre style={{
                  backgroundColor: '#f3f4f6',
                  padding: '10px',
                  borderRadius: '4px',
                  overflow: 'auto',
                  maxHeight: '200px',
                }}>
                  {formatValue(selectedMemory)}
                </pre>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <div>
                  <strong>Confidence:</strong> {selectedMemory.confidence}%
                </div>
                <div>
                  <strong>Sensitivity:</strong> {SENSITIVITY_LABELS[selectedMemory.sensitivity]}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <div>
                  <strong>Created:</strong>{' '}
                  {new Date(selectedMemory.created_at).toLocaleString()}
                </div>
                <div>
                  <strong>Updated:</strong>{' '}
                  {new Date(selectedMemory.updated_at).toLocaleString()}
                </div>
              </div>

              {selectedMemory.expires_at && (
                <div>
                  <strong>Expires:</strong>{' '}
                  <span style={{ color: isExpired(selectedMemory) ? '#ef4444' : '#6b7280' }}>
                    {new Date(selectedMemory.expires_at).toLocaleString()}
                    {isExpired(selectedMemory) && ' (Expired)'}
                  </span>
                </div>
              )}

              <div>
                <strong>Contact ID:</strong> {selectedMemory.contact_id}
              </div>

              {selectedMemory.entity_id && (
                <div>
                  <strong>Entity ID:</strong> {selectedMemory.entity_id}
                </div>
              )}
            </div>

            <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
              <button
                onClick={() => handleDeleteMemory(selectedMemory.id)}
                className="gca-button gca-button-danger"
              >
                Delete Memory
              </button>
              <button
                onClick={() => setSelectedMemory(null)}
                className="gca-button"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Info Section */}
      <div className="gca-card gca-info">
        <h3>ℹ️ About Long-Term Memory</h3>
        <p className="gca-subtext">
          The system automatically extracts and stores important information from conversations,
          building a long-term memory of each customer. This helps agents provide personalized
          service based on past interactions.
        </p>
        <div className="gca-steps">
          <strong>Memory Types:</strong>
          <ul style={{ marginTop: '10px' }}>
            <li><strong>Preferences:</strong> Customer likes, dislikes, and communication preferences</li>
            <li><strong>History:</strong> Past interactions, purchases, and service history</li>
            <li><strong>Risk:</strong> Payment issues, complaints, or flagged concerns</li>
            <li><strong>Escalation:</strong> Previous escalations and resolution status</li>
            <li><strong>Booking Context:</strong> Appointment scheduling preferences and history</li>
            <li><strong>Profile:</strong> Demographic information and contact details</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default MemoryManagement;
